import { shareTypeLabel, sortByShareType } from "@/common/share";
import { ButtonProps, ToggleButtonProps } from "@/components-v2/Button";
import { DataTableProps, TableColumn } from "@/components-v2/DataTable";
import { EmptyStateProps } from "@/components-v2/EmptyState";
import { IconName } from "@/components-v2/iconMapping";
import {
  calculateOwnershipInPercent,
  excelFileUploadConfig,
  shareholdersWithShares,
} from "@/dbot/common/commonDomain";
import { editWithExcelEvent, importFromExcelEvent } from "@/excel/excelDomain";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { I18nState } from "@/i18n/i18nDomain";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { RoleType } from "@/types/enums/RoleType";
import { ActorType } from "@/types/SelectActors";
import { makeCountrySelect } from "@/utils/countries";
import { CURRENCY_NOK } from "@/utils/currency";
import { when, whenNotEmpty } from "@/utils/functions";
import { isBoardMember } from "@/utils/Roles";
import { compact, max, noop, range, sumBy, uniqBy } from "lodash";
import { runInAction } from "mobx";
import { createElement } from "react";
import {
  CurrencyExchangeSource,
  currencyExchangeSourceLabels,
  currencyExchangeSourceOptions,
  kindContributionMethodOptions,
  KindContributionOption,
  kindContributionOptionsLabels,
} from "./contributionInKindDomain";
import { shareTypePriorityOrderLabel } from "./debtContributionDomain";
import {
  createAddShareholdersFromActorsModel,
  excessAmountMethodLabel,
  excessAmountMethodOptions,
  getSubscriptionProgress,
  IncreaseOfShareCapitalForm,
  IncreaseOfShareCapitalType,
  mapActorsToSubscriberForIssue,
  mapShareholderDTOToSubscriberForIssue,
  prepAddSubscribersDropdown,
  prepSignerSelectProps,
  QuickSetupType,
  ShareIssueModeModel,
  SignerChoice,
  signerChoiceLabel,
  SubscriberForCapitalIncrease,
  SubscriberForIncreaseNominalValueModel,
  SubscriberForIssueModel,
  SubscribersDialogState,
  SubscriptionForIssueModel,
  SubscriptionProgress,
} from "./increaseOfShareCapitalDomain";
import { showSuccess, thunk } from "@/events/eventsDomain";

function setState(form: IncreaseOfShareCapitalForm, state: SubscribersDialogState | undefined) {
  runInAction(() => {
    form.subscibersDialogState = state;
  });
}

function updateState(form: IncreaseOfShareCapitalForm, f: (state: SubscribersDialogState) => void) {
  runInAction(() => {
    if (form.subscibersDialogState) {
      f(form.subscibersDialogState);
    }
  });
}

export function isEmptyState(form: IncreaseOfShareCapitalForm) {
  return (
    form.increaseOfShareCapitalTypeModel.value === IncreaseOfShareCapitalType.shareIssue &&
    form.subscribersForIssueCandidates.length === 0
  );
}

export function openSubscribersDialog(
  form: IncreaseOfShareCapitalForm,
  options?: { applyQuickSettings?: boolean },
) {
  const state: SubscribersDialogState = {
    leftPanel: isEmptyState(form) ? "settings" : undefined,
    actorsModel: createAddShareholdersFromActorsModel(
      form.context.orgNo,
      form.dispatch,
      form.shareIssue.subscribers,
      form.shareIssue.existingShareholders,
      form.shareIssue.addSubscribers,
      noop,
    ),
  };
  if (state.actorsModel) {
    state.actorsModel.fetchActors(ActorType.BOARD_MEMBER);
    state.actorsModel.fetchActors(ActorType.CEO);
    state.actorsModel.fetchActors(ActorType.SHAREHOLDER);
  }
  if (options?.applyQuickSettings) {
    form.setQuickSetup(QuickSetupType.CASH_PRIVATE_PLACEMENT);
  }
  form.clearSubscriptionCompletionError();
  setState(form, state);
}

export function closeSubscribersDialog(form: IncreaseOfShareCapitalForm) {
  setState(form, undefined);
}

export function toggleLeftPanel(
  form: IncreaseOfShareCapitalForm,
  leftPanel?: SubscribersDialogState["leftPanel"],
) {
  updateState(form, (state) => {
    state.leftPanel = state.leftPanel === leftPanel ? undefined : leftPanel;
  });
}

export const leftPanels: Record<
  NonNullable<SubscribersDialogState["leftPanel"]>,
  { name: NonNullable<SubscribersDialogState["leftPanel"]>; icon: IconName; title: TMessage }
> = {
  ["settings"]: {
    name: "settings",
    icon: "gear",
    title: { key: "dbot.settings" },
  },
  ["key-figures"]: {
    name: "key-figures",
    icon: "trend-up",
    title: { key: "decisions.increaseShareCapital.summary.title" },
  },
};

export function prepLeftPanelButtons(
  form: IncreaseOfShareCapitalForm,
  state: SubscribersDialogState,
): [key: string, props: ToggleButtonProps][] {
  return Object.values(leftPanels).map((p) => [
    p.name,
    {
      icon: p.icon,
      variant: "basic",
      disableMinWidth: true,
      selected: state.leftPanel === p.name,
      tooltip: p.title,
      onClick: () =>
        updateState(form, (s) => {
          s.leftPanel = p.name;
        }),
    } satisfies ToggleButtonProps,
  ]);
}

export function prepShortcutButtons(
  form: IncreaseOfShareCapitalForm,
): [name: string, ButtonProps | undefined][] {
  const actorsModel = form.subscibersDialogState?.actorsModel;
  if (!actorsModel) {
    return [];
  }
  return [
    [
      "rights-issue",
      when(!form.isQuickSetupApplied, () => ({
        variant: "basic",
        icon: "plus",
        label: {
          key: "decisions.increaseShareCapital.quickSetupSelector.types.cashPreferentialRights.short",
        },
        onClick: () => form.setQuickSetup(QuickSetupType.CASH_PREFERENTIAL_RIGHTS),
      })),
    ],
    [
      "shareholders",
      whenNotEmpty(
        shareholdersWithShares(
          form.context.initialState.shareholders,
          form.context.inputState?.sharesForShareholders,
        ),
        (xs) => ({
          variant: "basic",
          icon: "plus",
          label: { key: "addShareholders", params: { n: xs.length } },
          onClick: () => form.shareIssue.addSubscribers(mapShareholderDTOToSubscriberForIssue(xs)),
        }),
      ),
    ],
    [
      "board-members",
      whenNotEmpty(
        actorsModel.actors.filter((x) => x.actor.roleType && isBoardMember(x.actor.roleType)),
        (actors) => ({
          variant: "basic",
          icon: "plus",
          label: { key: "addBoard", params: { n: actors.length } },
          onClick: () => {
            form.shareIssue.addSubscribers(
              mapActorsToSubscriberForIssue(actors.map((a) => a.toSelectedActor())),
            );
          },
        }),
      ),
    ],
    [
      "ceo",
      when(
        actorsModel.actors.find((x) => x.actor.type === ActorType.CEO),
        (actor) => ({
          variant: "basic",
          icon: "plus",
          label: { key: "ceo-with-name", params: { name: actor.actor.name } },
          onClick: () => {
            form.shareIssue.addSubscribers(
              mapActorsToSubscriberForIssue([actor.toSelectedActor()]),
            );
          },
        }),
      ),
    ],
    [
      "chairperson",
      when(
        actorsModel.actors.find((x) => x.actor.roleType === RoleType.CHAIRPERSON),
        (actor) => ({
          variant: "basic",
          icon: "plus",
          label: { key: "chairperson-with-name", params: { name: actor.actor.name } },
          onClick: () => {
            form.shareIssue.addSubscribers(
              mapActorsToSubscriberForIssue([actor.toSelectedActor()]),
            );
          },
        }),
      ),
    ],
  ];
}

export function editSubscribersWithExcel(form: IncreaseOfShareCapitalForm, i18n: I18nState) {
  form.dispatch(editWithExcelEvent("subscribers.xlsx", prepShareIssueSubscribersTable(form), i18n));
}

export function importSubscribersFileUploadConfig(
  form: IncreaseOfShareCapitalForm,
): FileUploadConfig {
  return excelFileUploadConfig(form);
}

export function importSubscribersFromExcel(
  form: IncreaseOfShareCapitalForm,
  files: File[],
  fileUploadConfig: FileUploadConfig,
  i18n: I18nState,
) {
  form.shareIssue.initShareholderEmailBatchUpdates();
  form.dispatch(
    importFromExcelEvent(
      files[0],
      fileUploadConfig,
      prepShareIssueSubscribersTable(form),
      i18n,
      ({ added }) => [
        thunk(() => {
          form.shareIssue.addSubscriberModels(added);
          form.shareIssue.updateShareholderEmailBatch();
        }),
        showSuccess({
          key: "increaseShareCapital.import-subscribers-summary",
          params: { count: added.length },
        }),
      ],
    ),
  );
}

export function prepShareIssueSubscribersTable(
  form: IncreaseOfShareCapitalForm,
): DataTableProps<SubscriberForIssueModel> {
  const subscribers = form.subscribersForIssueCandidates;
  return {
    variant: "EDITABLE",
    v2: true,
    withFooterRow: true,
    getId: (subscriber) => subscriber.subscriberid,
    tableData: subscribers,
    initNewRow: () => form.shareIssue.toSubscriberForIssueModel(form.shareIssue.createSubscriber()),
    tableColumns: [
      ...prepShareIssueSubscriberColumns(form, subscribers),
      ...prepShareholderSubscriptionColumns(form),
      ...prepDebtConversionColumns(form, subscribers),
      ...prepKindContributionColumns(form, subscribers),
      ...prepCashContributionColumns(form),
      ...prepSignerColumns(form, subscribers),
      ...prepAddressColumns(form),
    ],
    setEditableRef: (ref) => {
      form.shareIssue.setSelectSubscriberFn(
        ref
          ? (subscriber) => {
              ref.focusCell({ rowId: subscriber.subscriberid, colName: "name" });
            }
          : undefined,
      );
    },
  };
}

export function prepIncreaseNominalValueSubscribersTable(
  form: IncreaseOfShareCapitalForm,
): DataTableProps<SubscriberForIncreaseNominalValueModel> {
  const {
    increaseNominalValue: { subscribers },
  } = form;

  return {
    variant: "EDITABLE",
    v2: true,
    withFooterRow: true,
    getId: (subscriber) => subscriber.subscriberid,
    tableData: subscribers,
    tableColumns: [
      ...prepIncreaseNominalValueSubscriberColumns(form, subscribers),
      ...prepIncreaseNominalValueShareContributionColumns(form),
      ...prepDebtConversionColumns(form, subscribers),
      ...prepKindContributionColumns(form, subscribers),
      ...prepCashContributionColumns(form),
      ...prepSignerColumns(form, subscribers),
      ...prepAddressColumns(form),
    ],
  };
}

function prepIncreaseNominalValueSubscriberColumns(
  form: IncreaseOfShareCapitalForm,
  subscribers: SubscriberForIncreaseNominalValueModel[],
): TableColumn<SubscriberForIncreaseNominalValueModel>[] {
  const { context } = form;
  return [
    {
      name: "start",
      label: "",
      children: [
        {
          name: "shareholder-type",
          label: "",
          compact: true,
          align: "center",
          getValue: (subscriber, _, { t }) => [
            "icon",
            subscriber.isCompany
              ? {
                  name: "buildings",
                  "data-tooltip": t("decisions.increaseShareCapital.subscribersTable.company"),
                }
              : {
                  name: "user-square",
                  "data-tooltip": t("common.person"),
                },
          ],
          isReadonly: () => true,
        },
      ],
    },
    {
      name: "subscriber",
      label: { key: "general.subscriber" },
      align: "left",
      children: [
        {
          name: "name",
          label: { key: "decisions.increaseShareCapital.subscribersTable.companyOrIndividual" },
          getValue: (subscriber) => subscriber.nameField.validatedValue,
          getSortValue: (subscriber) => subscriber.nameField.validatedValue,
          exportToExcel: (subscriber) => ({
            type: "string",
            value: subscriber.nameField.validatedValue,
          }),
          getFilterValue: (subscriber) => subscriber.nameField.validatedValue,
          align: "left",
          isReadonly: true,
          getFooterValue: () => ({
            key: "decisions.increaseShareCapital.subscribersTable.numberOfSubscribers",
            params: { count: subscribers.length },
          }),
        },
        {
          name: "orgNoOrSsn",
          label: { key: "decisions.increaseShareCapital.subscribersTable.orgOrId" },
          getValue: (subscriber, _, { f }) =>
            when(subscriber.ssnOrCompanyNumber, (value) =>
              subscriber.isCompany ? f.formatOrgNo(value) : f.formatSsn(value),
            ),
          align: "left",
          isReadonly: () => true,
        },
        {
          name: "ownership",
          label: { key: "decisions.increaseShareCapital.subscribersTable.currentOwnership" },
          getValue: (subscriber, _, { f }) =>
            f.formatPercent(
              calculateOwnershipInPercent(
                context.initialState.sharesForShareholders,
                subscriber.shareholderId,
              ),
            ),
          align: "right",
          isReadonly: () => true,
        },
      ],
    },
  ];
}

function prepIncreaseNominalValueShareContributionColumns(
  form: IncreaseOfShareCapitalForm,
): TableColumn<SubscriberForIncreaseNominalValueModel>[] {
  return [
    {
      name: "share-contribution",
      label: { key: "decisions.increaseShareCapital.subscribersTable.shareContribution" },
      align: "left",
      children: compact([
        ...form.increaseNominalValue.shareTypes.map<
          TableColumn<SubscriberForIncreaseNominalValueModel>
        >((shareType) => ({
          name: `contribution-${shareType.shareTypeCode}`,
          label: shareTypeLabel(shareType),
          getValue: (subscriber: SubscriberForIncreaseNominalValueModel) => ({
            type: "number",
            value: subscriber.totalPriceForShareType(shareType.sharetypeId),
          }),
          getFooterValue: () => ({
            type: "number",
            value: sumBy(form.increaseNominalValue.subscribers, (s) =>
              s.totalPriceForShareType(shareType.sharetypeId),
            ),
          }),
          align: "right",
          isReadonly: () => true,
        })),
        form.increaseNominalValue.shareTypes.length > 1 &&
          ({
            name: "total-contribution",
            label: { key: "total" },
            getValue: (subscriber) => ({
              type: "number",
              value: subscriber.totalPriceForSubscriber,
            }),
            getFooterValue: () => ({
              type: "number",
              value: sumBy(form.increaseNominalValue.subscribers, (s) => s.totalPriceForSubscriber),
            }),
            align: "right",
            isReadonly: () => true,
          } satisfies TableColumn<SubscriberForIncreaseNominalValueModel>),
      ]),
    },
  ];
}

function prepShareIssueSubscriberColumns(
  form: IncreaseOfShareCapitalForm,
  subscribers: SubscriberForIssueModel[],
): TableColumn<SubscriberForIssueModel>[] {
  const { context, shareIssue } = form;
  return [
    {
      name: "start",
      label: "",
      children: compact([
        !form.isPreferentialRight && {
          name: "start-actions",
          label: "",
          compact: true,
          align: "center",
          getValue: (subscriber) => [
            "icon-button",
            {
              icon: "trash",
              tooltip: { key: "remove" },
              onClick: () => shareIssue.deleteSubscriber(subscriber),
              disabled: form.isProjectLocked,
            },
          ],
          getFooterValue: form.isPreferentialRight
            ? undefined
            : () => ["dropdown", prepAddSubscribersDropdown({ form, variant: "mini-primary" })],
        },
        {
          name: "shareholder-type",
          label: { key: "decisions.increaseShareCapital.subscribersTable.shareholderType" },
          compact: true,
          align: "center",
          isReadonly: true,
          getValue: (subscriber, _, { t }) => [
            "icon",
            subscriber.isCompany
              ? {
                  name: "buildings",
                  "data-tooltip": t("decisions.increaseShareCapital.subscribersTable.company"),
                }
              : {
                  name: "user-square",
                  "data-tooltip": t("common.person"),
                },
          ],
          exportToExcel: (subscriber, _, { t }) => ({
            type: "string",
            value: subscriber.isCompany
              ? t("decisions.increaseShareCapital.subscribersTable.company")
              : t("common.person"),
          }),
          excelColumnConstraints: ({ t }) => [
            {
              type: "list-constraint",
              values: [
                t("decisions.increaseShareCapital.subscribersTable.company"),
                t("common.person"),
              ],
            },
          ],
          importFromExcel: (subscriber, data, { isNewRow, i18n: { t } }) => {
            if (isNewRow && data.type === "string" && "setIsCompany" in subscriber) {
              subscriber.setIsCompany(
                data.value === t("decisions.increaseShareCapital.subscribersTable.company"),
              );
            }
          },
        },
      ]),
    },
    {
      name: "subscriber",
      label: { key: "general.subscriber" },
      align: "left",
      children: [
        {
          name: "name",
          label: { key: "decisions.increaseShareCapital.subscribersTable.companyOrIndividual" },
          getValue: (subscriber) => ["mtext-input", { model: subscriber.nameField }],
          getSortValue: (subscriber) => subscriber.nameField.validatedValue,
          getFilterValue: (subscriber) => subscriber.nameField.validatedValue,
          align: "left",
          isReadonly: (subscriber) => subscriber.isExisting,
          getFooterValue: () => ({
            key: "decisions.increaseShareCapital.subscribersTable.numberOfSubscribers",
            params: { count: subscribers.length },
          }),
          exportToExcel: (subscriber) => ({
            type: "string",
            value: subscriber.nameField.validatedValue,
          }),
          importFromExcel: (subscriber, data) => {
            if (!subscriber.isExisting && data.type === "string" && data.value) {
              subscriber.nameField.setAndCommitInputValue(data.value);
            }
          },
        },
        {
          name: "nationality",
          label: { key: "decisions.increaseShareCapital.subscribersTable.nationality" },
          getValue: (subscriber) => [
            "select",
            makeCountrySelect(
              {
                value: subscriber.isExisting
                  ? subscriber.nationalityFromExistingShareholder
                  : subscriber.nationality.inputValue,
                onSelect: subscriber.nationality.setAndCommitInputValue,
                disabled: subscriber.isExisting,
              },
              { compact: true },
            ),
          ],
          getSortValue: (subscriber) => subscriber.nationality.inputValue,
          getFilterValue: (subscriber) => subscriber.nationality.inputValue,
          align: "left",
          isReadonly: (subscriber) => subscriber.isExisting,
          exportToExcel: (subscriber) => ({
            type: "string",
            value: subscriber.isExisting
              ? subscriber.nationalityFromExistingShareholder
              : subscriber.nationality.inputValue,
          }),
          importFromExcel: (subscriber, data) => {
            if (!subscriber.isExisting && data.type === "string" && data.value) {
              subscriber.nationality.setAndCommitInputValue(data.value);
            }
          },
        },
        {
          name: "orgNoOrSsn",
          label: { key: "decisions.increaseShareCapital.subscribersTable.orgOrId" },
          getValue: (subscriber) =>
            subscriber.isCompany
              ? ["mtext-input", { model: subscriber.organisationNumber }]
              : [
                  "mtext-input",
                  { model: subscriber.nationalId },
                  {
                    displayValue: () => ({
                      type: "ssn",
                      value: subscriber.nationalId.inputValue,
                      options: { nationality: subscriber.nationality.validatedValue },
                    }),
                  },
                ],
          getSortValue: (subscriber) => subscriber.ssnOrCompanyNumber,
          getFilterValue: (subscriber) => subscriber.ssnOrCompanyNumber,
          align: "left",
          isReadonly: (subscriber) => subscriber.isExisting,
          exportToExcel: (subscriber, _, { f }) => ({
            type: "string",
            value: subscriber.isCompany
              ? subscriber.organisationNumber.inputValue
              : subscriber.isExisting
                ? f.formatSsn(subscriber.nationalId.inputValue, {
                    nationality: subscriber.nationality.validatedValue,
                  })
                : subscriber.nationalId.inputValue,
          }),
          importFromExcel: (subscriber, data) => {
            if (
              !subscriber.isExisting &&
              (data.type === "string" || data.type === "numeric") &&
              data.value !== undefined
            ) {
              const value = String(data.value);
              if (subscriber.isCompany) {
                subscriber.organisationNumber.setAndCommitInputValue(value);
              } else {
                subscriber.nationalId.setAndCommitInputValue(value);
              }
            }
          },
        },
        {
          name: "email",
          label: { key: "email" },
          getValue: (subscriber) => [
            "mtext-input",
            { model: subscriber.email },
            {
              onCommit: () => {
                if (
                  subscriber.shareholderId &&
                  subscriber.email.isValid &&
                  subscriber.email.isChanged
                ) {
                  form.shareIssue.updateShareholderEmail(
                    subscriber.shareholderId,
                    subscriber.email.validatedValue,
                  );
                  subscriber.email.rebase();
                }
              },
            },
          ],
          getSortValue: (subscriber) => subscriber.email.validatedValue,
          getFilterValue: (subscriber) => subscriber.email.validatedValue,
          align: "left",
          exportToExcel: (subscriber) => ({
            type: "string",
            value: subscriber.email.validatedValue,
          }),
          importFromExcel: (subscriber, data) => {
            if (data.type === "string" && data.value) {
              subscriber.email.setAndCommitInputValue(data.value);
              if (
                subscriber.shareholderId &&
                subscriber.email.isValid &&
                subscriber.email.isChanged
              ) {
                form.shareIssue.queueShareholderEmailUpdate(subscriber.shareholderId, data.value);
                subscriber.email.rebase();
              }
            }
          },
        },
        {
          name: "ownership",
          label: { key: "decisions.increaseShareCapital.subscribersTable.currentOwnership" },
          getValue: (subscriber, _, { f }) =>
            when(subscriber.shareholderId, (shareholderId) =>
              f.formatPercent(
                calculateOwnershipInPercent(
                  context.initialState.sharesForShareholders,
                  shareholderId,
                ),
              ),
            ),
          getSortValue: (subscriber) =>
            when(subscriber.shareholderId, (shareholderId) =>
              calculateOwnershipInPercent(
                context.initialState.sharesForShareholders,
                shareholderId,
              ),
            ),
          exportToExcel: (subscriber) => ({
            type: "numeric",
            value: when(subscriber.shareholderId, (shareholderId) =>
              calculateOwnershipInPercent(
                context.initialState.sharesForShareholders,
                shareholderId,
              ),
            ),
            format: "PERCENT",
          }),
          align: "right",
          isReadonly: () => true,
        },
      ],
    },
  ];
}

const prefixLabel = (prefix: TMessage, label: TMessage): TMessageParam => ({
  type: "text",
  value: "{{prefix}}: {{label}}",
  params: { prefix, label },
});

function prepShareholderSubscriptionColumns(
  form: IncreaseOfShareCapitalForm,
): TableColumn<SubscriberForIssueModel>[] {
  const { shareIssue, subscribersForIssueCandidates: subscribers, isPreferentialRight } = form;

  return shareIssue.selectedShareTypes.selected.map<TableColumn<SubscriberForIssueModel>>(
    (model) => {
      const withSubscription = <V>(
        subscriber: SubscriberForIssueModel,
        fn: (subscription: SubscriptionForIssueModel) => V,
      ) =>
        when(subscriber.getSubscriptionForShareType(model.sharetypeId), (subscription) =>
          fn(subscription),
        );
      const isIndividualized = !isPreferentialRight && model.model.isIndividualSharePrice.value;
      return {
        name: model.sharetypeCode,
        label: { type: "shareType", value: model.shareType },
        align: "left",
        children: [
          {
            name: model.sharetypeCode,
            label: { key: "general.number-of-shares" },
            exportToExcelLabel: shareTypeLabel(model.shareType),
            align: "right",
            getValue: (subscriber) =>
              withSubscription(subscriber, (subscription) => [
                "mtext-input",
                {
                  model: subscription.numberOfShares,
                  testId: `numberOfShares-${model.sharetypeCode}`,
                },
              ]),
            getSortValue: (subscriber) =>
              withSubscription(
                subscriber,
                (subscription) => subscription.numberOfShares.parsedValue,
              ),
            exportToExcel: (subscriber) => ({
              type: "numeric",
              value: withSubscription(
                subscriber,
                (subscription) => subscription.numberOfShares.parsedValue,
              ),
            }),
            importFromExcel: (row, data) => {
              withSubscription(row, (subscription) => {
                if (data.type === "numeric") {
                  subscription.numberOfShares.setAndCommitInputValue(data.value);
                }
              });
            },
            getFooterValue: () => ({
              type: "number",
              value: sumBy(subscribers, (s) => s.getNumberOfSharesForShareType(model.sharetypeId)),
            }),
            getSecondaryFooterValue: ({ t }) =>
              when(
                form.shareIssue.selectedShareTypes.selected.find(
                  (s) => s.sharetypeId === model.sharetypeId,
                )?.model,
                (model) =>
                  when(
                    toAllocationStatus(
                      model.issueMode,
                      getSubscriptionProgress(
                        form.calculateIssuedSharesForActiveSubscribers(model),
                        model,
                      ),
                    ),
                    ({ className, text }) => createElement("span", { className }, t(text)),
                  ),
              ),
          },
          {
            name: `${model.sharetypeCode}-price-per-share`,
            label: {
              key: "decisions.increaseShareCapital.subscribersTable.currencyPerShare",
              params: { currency: CURRENCY_NOK },
            },
            getValue: (subscriber) =>
              withSubscription(subscriber, (subscription) => [
                "mtext-input",
                {
                  model: isIndividualized ? subscription.subscriptionPrice : model.model.sharePrice,
                  align: "right",
                },
              ]),
            getSortValue: (subscriber) =>
              withSubscription(
                subscriber,
                (subscription) =>
                  (isIndividualized ? subscription.subscriptionPrice : model.model.sharePrice)
                    .parsedValue,
              ),
            exportToExcel: (subscriber) => ({
              type: "numeric",
              value: withSubscription(
                subscriber,
                (subscription) =>
                  (isIndividualized ? subscription.subscriptionPrice : model.model.sharePrice)
                    .parsedValue,
              ),
              readonly: !isIndividualized,
            }),
            importFromExcel: (subscriber, data) => {
              withSubscription(subscriber, (subscription) => {
                if (isIndividualized && data.type === "numeric" && data.value !== undefined) {
                  subscription.subscriptionPrice.setAndCommitInputValue(data.value);
                }
              });
            },
            align: "right",
          },
          {
            name: `${model.sharetypeCode}-total-contribution`,
            label: { key: "decisions.increaseShareCapital.subscribersTable.shareContribution" },
            getValue: (subscriber) =>
              withSubscription(subscriber, (subscription) => ({
                type: "number",
                value: subscription.totalPrice,
              })),
            getSortValue: (subscriber) =>
              withSubscription(subscriber, (subscription) => subscription.totalPrice),
            exportToExcel: (_, { row, col, addr }) => ({
              type: "formula",
              value: `${addr(col - 2, row)}*${addr(col - 1, row)}`,
            }),
            getFooterValue: () => ({
              type: "number",
              value: sumBy(subscribers, (s) => s.getTotalPriceForShareType(model.sharetypeCode)),
            }),
            align: "right",
            isReadonly: true,
          },
        ],
      };
    },
  );
}

export function prepDebtConversionColumns<T extends SubscriberForCapitalIncrease>(
  form: IncreaseOfShareCapitalForm,
  subscribers: T[],
): TableColumn<T>[] {
  if (!form.isDebtContributionActive) {
    return [];
  }
  return [
    {
      name: "debt-conversion",
      label: { key: "decisions.increaseShareCapital.subscribersTable.debtConversion" },
      align: "left",
      children: compact<TableColumn<T>>([
        {
          name: "debt-conversion-total-claim",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.debtConversion.totalClaim",
          },
          exportToExcelLabel: prefixLabel(
            { key: "decisions.increaseShareCapital.subscribersTable.debtConversion" },
            {
              key: "decisions.increaseShareCapital.subscribersTable.debtConversion.totalClaim",
            },
          ),
          getValue: (subscriber) => [
            "mtext-input",
            { model: subscriber.debtContributionModel.totalAmountReceivable, align: "right" },
          ],
          getSortValue: (subscriber) =>
            subscriber.debtContributionModel.totalAmountReceivable.toNumberOrZero(),
          getFooterValue: () =>
            sumBy(subscribers, (s) =>
              s.debtContributionModel.totalAmountReceivable.toNumberOrZero(),
            ),
          align: "right",
          exportToExcel: (subscriber) => ({
            type: "numeric",
            value: subscriber.debtContributionModel.totalAmountReceivable.toNumberOrZero(),
          }),
          importFromExcel: (subscriber, data) => {
            if (data.type === "numeric" && data.value !== undefined) {
              subscriber.debtContributionModel.totalAmountReceivable.setAndCommitInputValue(
                data.value,
              );
            }
          },
        },
        {
          name: "debt-conversion-max",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.debtConversion.maxConverted",
          },
          getValue: (subscriber) => [
            "mtext-input",
            { model: subscriber.debtContributionModel.maxAmountToBeConverted, align: "right" },
          ],
          getSortValue: (subscriber) =>
            subscriber.debtContributionModel.maxAmountToBeConverted.toNumberOrZero(),
          getFooterValue: () => ({
            type: "number",
            value: sumBy(subscribers, (s) =>
              s.debtContributionModel.maxAmountToBeConverted.toNumberOrZero(),
            ),
          }),
          align: "right",
          exportToExcel: (subscriber) => ({
            type: "numeric",
            value: subscriber.debtContributionModel.maxAmountToBeConverted.toNumberOrZero(),
          }),
          importFromExcel: (subscriber, data) => {
            if (data.type === "numeric" && data.value !== undefined) {
              subscriber.debtContributionModel.maxAmountToBeConverted.setAndCommitInputValue(
                data.value,
              );
            }
          },
        },
      ]),
    },
    {
      name: "debt-conversion-excess-amount",
      label: { key: "decisions.increaseShareCapital.subscribersTable.debtConversion.excessAmount" },
      align: "left",
      children: [
        {
          name: "debt-conversion-excess-amount-remaining",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.debtConversion.excessAmountRemaining",
          },
          exportToExcelLabel: prefixLabel(
            {
              key: "decisions.increaseShareCapital.subscribersTable.debtConversion.excessAmountRemaining",
            },
            { key: "decisions.increaseShareCapital.subscribersTable.debtConversion.excessAmount" },
          ),
          getValue: (subscriber) => ({
            type: "number",
            value: subscriber.debtContributionModel.excessAmount,
          }),
          getSortValue: (subscriber) => subscriber.debtContributionModel.excessAmount,
          getFooterValue: () => ({
            type: "number",
            value: sumBy(subscribers, (s) => s.debtContributionModel.excessAmount),
          }),
          align: "right",
          isReadonly: () => true,
        },
        {
          name: "debt-conversion-excess-amount-method",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.debtConversion.excessAmountMethod",
          },
          exportToExcelLabel: prefixLabel(
            { key: "decisions.increaseShareCapital.subscribersTable.debtConversion.excessAmount" },
            {
              key: "decisions.increaseShareCapital.subscribersTable.debtConversion.excessAmountMethod",
            },
          ),
          getValue: (subscriber) => [
            "mselect",
            {
              model: subscriber.debtContributionModel.excessAmountMethod,
              disabled: !subscriber.debtContributionModel.excessAmount,
            },
          ],
          getSortValue: (subscriber) =>
            subscriber.debtContributionModel.excessAmountMethod.getLabel(
              subscriber.debtContributionModel.excessAmountMethod.value,
            ),
          align: "left",
          isReadonly: (subscriber) => !subscriber.debtContributionModel.excessAmount,
          exportToExcel: (subscriber, _, { t }) => ({
            type: "string",
            value: when(subscriber.debtContributionModel.excessAmountMethod.value, (val) =>
              t(excessAmountMethodLabel[val]),
            ),
          }),
          excelColumnConstraints: ({ t }) => [
            {
              type: "list-constraint",
              values: excessAmountMethodOptions().map((x) => t(excessAmountMethodLabel[x])),
            },
          ],
          importFromExcel: (subscriber, data, { i18n: { t } }) => {
            if (
              subscriber.debtContributionModel.excessAmount &&
              data.type === "string" &&
              data.value
            ) {
              const option = excessAmountMethodOptions().find(
                (x) => t(excessAmountMethodLabel[x]) === data.value,
              );
              if (option) {
                subscriber.debtContributionModel.excessAmountMethod.setValue(option);
              }
            }
          },
        },
      ],
    },
    ...debtConversionSharesReceivedGroup(form, subscribers),
  ];
}

function debtConversionSharesReceivedGroup<T extends SubscriberForCapitalIncrease>(
  form: IncreaseOfShareCapitalForm,
  subscribers: T[],
): TableColumn<T>[] {
  const shareTypes = sortByShareType(
    uniqBy(
      subscribers.flatMap((s) => s.debtContributionModel.shareTypes),
      (s) => s.sharetypeId,
    ),
  );
  if (!shareTypes.length) {
    return [];
  }
  const example = subscribers[0].debtContributionModel;
  return [
    {
      name: "debt-conversion-shares-received",
      label: {
        key: "decisions.increaseShareCapital.subscribersTable.debtConversion.sharesReceived",
      },
      align: "left",
      children: compact<TableColumn<T>>([
        form.contributionTypes.selected.length > 1 &&
          subscribers.some((s) => s.debtContributionModel.showShareTypePriorityOrder) && {
            name: "debt-conversion-share-type-priority-order",
            label: { key: "subscriber.debtConversion.shareTypePriorityOrder" },
            exportToExcelLabel: prefixLabel(
              {
                key: "decisions.increaseShareCapital.subscribersTable.debtConversion.sharesReceived",
              },
              { key: "subscriber.debtConversion.shareTypePriorityOrder" },
            ),
            getValue: (subscriber: T) => [
              "mselect",
              { model: subscriber.debtContributionModel.shareTypePriorityOrder },
            ],
            exportToExcel: (subscriber: T, _, { t }) => ({
              type: "string",
              value: t(
                shareTypePriorityOrderLabel(
                  subscriber.debtContributionModel.shareTypePriorityOrder.value,
                ),
              ),
            }),
            excelColumnConstraints: ({ tf }) => [
              {
                type: "list-constraint",
                values: example.shareTypePriorityOrderOptions.map((x) =>
                  tf(shareTypePriorityOrderLabel(x)),
                ),
              },
            ],
            importFromExcel: (subscriber, data, { i18n: { tf } }) => {
              if (data.type === "string" && data.value) {
                const option = subscriber.debtContributionModel.shareTypePriorityOrderOptions.find(
                  (x) => tf(shareTypePriorityOrderLabel(x)) === data.value,
                );
                if (option) {
                  subscriber.debtContributionModel.shareTypePriorityOrder.setValue(option);
                }
              }
            },
          },
        ...shareTypes.map<TableColumn<T>>((shareType) => ({
          name: `debt-conversion-shares-received-${shareType.sharetypeId}`,
          label: { type: "shareType", value: shareType },
          getValue: (subscriber) => ({
            type: "number",
            value: subscriber.debtContributionModel.subscribedSharesForShareType(shareType),
          }),
          getFooterValue: () => ({
            type: "number",
            value: sumBy(subscribers, (subscriber) =>
              subscriber.debtContributionModel.subscribedSharesForShareType(shareType),
            ),
          }),
          isReadonly: () => true,
          align: "right",
        })),
      ]),
    },
  ];
}

function hasCurrencyContribution(subscribers: SubscriberForCapitalIncrease[]) {
  return subscribers.some((s) => s.hasCurrencyContributionInKind);
}

function hasOtherExchangeRateSource(subscribers: SubscriberForCapitalIncrease[]) {
  return subscribers.some(
    (s) =>
      s.kindContributionModel.isCurrencyContribution &&
      s.kindContributionModel.currencyContribution.source.value === CurrencyExchangeSource.OTHER,
  );
}

function hasOtherCurrencyContribution(subscribers: SubscriberForCapitalIncrease[]) {
  return subscribers.some((s) => s.kindContributionModel.isOtherCurrencyContribution);
}

function hasOtherKindContribution(subscribers: SubscriberForCapitalIncrease[]) {
  return subscribers.some((s) => s.kindContributionModel.isOtherKindContribution);
}

function kindContributionMethodLabel(item: KindContributionOption | null): TMessage {
  return item
    ? kindContributionOptionsLabels[item]
    : {
        key: "decisions.increaseShareCapital.subscribersTable.kindContribution.none",
      };
}

export function prepKindContributionColumns<T extends SubscriberForCapitalIncrease>(
  { context, isKindContributionActive }: IncreaseOfShareCapitalForm,
  subscribers: T[],
): TableColumn<T>[] {
  if (!isKindContributionActive) {
    return [];
  }
  return compact<TableColumn<T>>([
    {
      name: "kind-contribution",
      label: { key: "decisions.increaseShareCapital.subscribersTable.kindContribution" },
      align: "left",
      children: [
        {
          name: "kind-contribution-type",
          label: { key: "type" },
          exportToExcelLabel: {
            key: "decisions.increaseShareCapital.subscribersTable.kindContribution",
          },
          getValue: (subscriber) => [
            "mselect",
            {
              model: subscriber.kindContributionModel.kindContributionMethod,
              labelFn: kindContributionMethodLabel,
            },
          ],
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.kindContributionMethod.getLabel(
              subscriber.kindContributionModel.kindContributionMethod.value,
            ),
          align: "left",
          exportToExcel: (subscriber, _, { t }) => ({
            type: "string",
            value: t(
              kindContributionMethodLabel(
                subscriber.kindContributionModel.kindContributionMethod.value,
              ),
            ),
          }),
          excelColumnConstraints: ({ t }) => [
            {
              type: "list-constraint",
              values: kindContributionMethodOptions().map((x) => t(kindContributionMethodLabel(x))),
            },
          ],
          importFromExcel: (subscriber, data, { i18n: { t } }) => {
            if (data.type === "string" && data.value) {
              const option = kindContributionMethodOptions().find(
                (x) => t(kindContributionMethodLabel(x)) === data.value,
              );
              if (option) {
                subscriber.kindContributionModel.kindContributionMethod.setValue(option);
              }
            }
          },
        },
      ],
    },
    hasCurrencyContribution(subscribers) && {
      name: "kind-contribution-currency",
      label: { key: "decisions.increaseShareCapital.subscribersTable.kindContribution.currency" },
      align: "left",
      children: compact<TableColumn<T>>([
        hasOtherCurrencyContribution(subscribers) && {
          name: "kind-contribution-other-currency",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.kindContribution.otherCurrency",
          },
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isOtherCurrencyContribution
              ? [
                  "mtext-input",
                  { model: subscriber.kindContributionModel.currencyContribution.currency },
                ]
              : undefined,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isOtherCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.currency.validatedValue
              : undefined,
          isReadonly: (subscriber) => !subscriber.kindContributionModel.isOtherCurrencyContribution,
          align: "left",
          exportToExcel: (subscriber) => ({
            type: "string",
            value: subscriber.kindContributionModel.isOtherCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.currency.validatedValue
              : undefined,
            readonly: false,
          }),
          importFromExcel: (subscriber, data) => {
            if (
              subscriber.kindContributionModel.isOtherCurrencyContribution &&
              data.type === "string" &&
              data.value
            ) {
              subscriber.kindContributionModel.currencyContribution.currency.setAndCommitInputValue(
                data.value,
              );
            }
          },
        },
        {
          name: "kind-contribution-amount",
          label: { key: "decisions.increaseShareCapital.subscribersTable.kindContribution.amount" },
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? [
                  "mtext-input",
                  {
                    model: subscriber.kindContributionModel.currencyContribution.value,
                    align: "right",
                  },
                ]
              : undefined,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.value.toNumberOrZero()
              : undefined,
          isReadonly: (subscriber) => !subscriber.kindContributionModel.isCurrencyContribution,
          align: "right",
          exportToExcel: (subscriber) => ({
            type: "numeric",
            value: subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.value.toNumber()
              : undefined,
            readonly: false,
          }),
          importFromExcel: (subscriber, data) => {
            if (
              subscriber.kindContributionModel.isCurrencyContribution &&
              data.type === "numeric" &&
              data.value !== undefined
            ) {
              subscriber.kindContributionModel.currencyContribution.value.setAndCommitInputValue(
                data.value,
              );
            }
          },
        },
        {
          name: "kind-contribution-exchange-rate",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.kindContribution.exchangeRate",
          },
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? [
                  "mtext-input",
                  {
                    model: subscriber.kindContributionModel.currencyContribution.exchangeRate,
                    align: "right",
                  },
                ]
              : undefined,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.exchangeRate.toNumberOrZero()
              : undefined,
          isReadonly: (subscriber) => !subscriber.kindContributionModel.isCurrencyContribution,
          align: "right",
          exportToExcel: (subscriber) => ({
            type: "numeric",
            value: subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.exchangeRate.toNumber()
              : undefined,
            readonly: false,
          }),
          importFromExcel: (subscriber, data) => {
            if (
              subscriber.kindContributionModel.isCurrencyContribution &&
              data.type === "numeric" &&
              data.value
            ) {
              subscriber.kindContributionModel.currencyContribution.exchangeRate.setAndCommitInputValue(
                data.value,
              );
            }
          },
        },
        {
          name: "kind-contribution-source",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.kindContribution.exchangeSource",
          },
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? [
                  "mselect",
                  {
                    model: subscriber.kindContributionModel.currencyContribution.source,
                  },
                ]
              : undefined,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.source.getLabel(
                  subscriber.kindContributionModel.currencyContribution.source.value,
                )
              : undefined,
          isReadonly: (subscriber) => !subscriber.kindContributionModel.isCurrencyContribution,
          align: "left",
          exportToExcel: (subscriber, _, { t }) => ({
            type: "string",
            value: subscriber.kindContributionModel.isCurrencyContribution
              ? t(
                  currencyExchangeSourceLabels[
                    subscriber.kindContributionModel.currencyContribution.source.value
                  ],
                )
              : undefined,
            readonly: false,
          }),
          excelColumnConstraints: ({ t }) => [
            {
              type: "list-constraint",
              values: currencyExchangeSourceOptions().map((x) =>
                t(currencyExchangeSourceLabels[x]),
              ),
              readonly: false,
            },
          ],
          importFromExcel: (subscriber, data, { i18n: { t } }) => {
            if (
              subscriber.kindContributionModel.isCurrencyContribution &&
              data.type === "string" &&
              data.value
            ) {
              const option = currencyExchangeSourceOptions().find(
                (x) => t(currencyExchangeSourceLabels[x]) === data.value,
              );
              if (option) {
                subscriber.kindContributionModel.currencyContribution.source.setValue(option);
              }
            }
          },
        },
        hasOtherExchangeRateSource(subscribers) && {
          name: "kind-contribution-other-source",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.kindContribution.otherSource",
          },
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution &&
            subscriber.kindContributionModel.currencyContribution.source.value ===
              CurrencyExchangeSource.OTHER
              ? [
                  "mtext-input",
                  {
                    model: subscriber.kindContributionModel.currencyContribution.otherSource,
                  },
                ]
              : undefined,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution &&
            subscriber.kindContributionModel.currencyContribution.source.value ===
              CurrencyExchangeSource.OTHER
              ? subscriber.kindContributionModel.currencyContribution.otherSource.validatedValue
              : undefined,
          isReadonly: (subscriber) =>
            !subscriber.kindContributionModel.isCurrencyContribution ||
            subscriber.kindContributionModel.currencyContribution.source.value !==
              CurrencyExchangeSource.OTHER,
          align: "left",
          exportToExcel: (subscriber) => ({
            type: "string",
            value:
              subscriber.kindContributionModel.isCurrencyContribution &&
              subscriber.kindContributionModel.currencyContribution.source.value ===
                CurrencyExchangeSource.OTHER
                ? subscriber.kindContributionModel.currencyContribution.otherSource.validatedValue
                : undefined,
            readonly: false,
          }),
          importFromExcel: (subscriber, data) => {
            if (
              subscriber.kindContributionModel.isCurrencyContribution &&
              subscriber.kindContributionModel.currencyContribution.source.value ===
                CurrencyExchangeSource.OTHER &&
              data.type === "string" &&
              data.value
            ) {
              subscriber.kindContributionModel.currencyContribution.otherSource.setAndCommitInputValue(
                data.value,
              );
            }
          },
        },
        {
          name: "kind-contribution-source-date",
          label: { key: "decisions.increaseShareCapital.subscribersTable.kindContribution.date" },
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? [
                  "mdate-input",
                  {
                    model: subscriber.kindContributionModel.currencyContribution.sourceDate,
                    align: "right",
                  },
                ]
              : undefined,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.sourceDate.validatedValue
              : undefined,
          isReadonly: (subscriber) => !subscriber.kindContributionModel.isCurrencyContribution,
          align: "right",
          exportToExcel: (subscriber) => ({
            type: "date",
            value: subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.currencyContribution.sourceDate.toISOString()
              : undefined,
            readonly: false,
          }),
          importFromExcel: (subscriber, data) => {
            if (
              subscriber.kindContributionModel.isCurrencyContribution &&
              data.type === "date" &&
              data.value
            ) {
              subscriber.kindContributionModel.currencyContribution.sourceDate.setAndCommitInputValue(
                data.value,
              );
            }
          },
        },
        {
          name: "kind-contribution-value",
          label: { key: "decisions.increaseShareCapital.subscribersTable.kindContribution.value" },
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? {
                  type: "amount",
                  value: subscriber.kindContributionModel.value.toNumber(),
                  currency: CURRENCY_NOK,
                }
              : undefined,
          isReadonly: () => true,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isCurrencyContribution
              ? subscriber.kindContributionModel.value.toNumber()
              : undefined,
          getFooterValue: () => ({
            type: "number",
            value: sumBy(subscribers, (s) =>
              s.kindContributionModel.isCurrencyContribution
                ? s.kindContributionModel.value.toNumberOrZero()
                : 0,
            ),
          }),
          align: "right",
        },
      ]),
    },
    hasOtherKindContribution(subscribers) && {
      name: "kind-contribution-other",
      label: { key: "decisions.increaseShareCapital.subscribersTable.kindContribution.other" },
      align: "left",
      children: [
        {
          name: "kind-contribution-other-value",
          label: {
            key: "decisions.increaseShareCapital.subscribersTable.kindContribution.otherValue",
          },
          exportToExcelLabel: prefixLabel(
            { key: "decisions.increaseShareCapital.subscribersTable.kindContribution.other" },
            {
              key: "decisions.increaseShareCapital.subscribersTable.kindContribution.otherValue",
            },
          ),
          getValue: (subscriber) =>
            subscriber.kindContributionModel.isOtherKindContribution
              ? [
                  "mtext-input",
                  {
                    model: subscriber.kindContributionModel.value,
                    align: "right",
                  },
                ]
              : undefined,
          getSortValue: (subscriber) =>
            subscriber.kindContributionModel.isOtherKindContribution
              ? subscriber.kindContributionModel.value.toNumberOrZero()
              : undefined,
          isReadonly: (subscriber) => !subscriber.kindContributionModel.isOtherKindContribution,
          getFooterValue: () => ({
            type: "number",
            value: sumBy(subscribers, (s) =>
              s.kindContributionModel.isOtherKindContribution
                ? s.kindContributionModel.value.toNumberOrZero()
                : 0,
            ),
          }),
          align: "right",
          exportToExcel: (subscriber) => ({
            type: "numeric",
            value: subscriber.kindContributionModel.isOtherKindContribution
              ? subscriber.kindContributionModel.value.toNumber()
              : undefined,
            readonly: false,
          }),
          importFromExcel: (subscriber, data) => {
            if (
              subscriber.kindContributionModel.isOtherKindContribution &&
              data.type === "numeric" &&
              data.value !== undefined
            ) {
              subscriber.kindContributionModel.value.setAndCommitInputValue(data.value);
            }
          },
        },
        ...context.documentLanguages.map<TableColumn<T>>(
          (language, _, coll) =>
            ({
              name: `kind-contribution-other-description-${language}`,
              label: {
                key: "decisions.increaseShareCapital.subscribersTable.kindContribution.otherDescription",
                params: { language: language.toUpperCase(), count: coll.length },
              },
              getValue: (subscriber) =>
                when(
                  subscriber.kindContributionModel.isOtherKindContribution &&
                    subscriber.kindContributionModel.description.textModels.find(
                      (t) => t.locale === language,
                    )?.model,
                  (model) => [
                    "mtext-input",
                    { model: model, inputProps: { className: "min-w-[30ch]" } },
                  ],
                ),
              getSortValue: (subscriber) =>
                subscriber.kindContributionModel.isOtherKindContribution
                  ? subscriber.kindContributionModel.description.textModels.find(
                      (t) => t.locale === language,
                    )?.model.validatedValue
                  : undefined,
              isReadonly: (subscriber) => !subscriber.kindContributionModel.isOtherKindContribution,
              align: "left",
              exportToExcel: (subscriber) => ({
                type: "string",
                value: subscriber.kindContributionModel.isOtherKindContribution
                  ? subscriber.kindContributionModel.description.textModels.find(
                      (t) => t.locale === language,
                    )?.model.validatedValue
                  : undefined,
                readonly: false,
              }),
              importFromExcel: (subscriber, data) => {
                if (
                  subscriber.kindContributionModel.isOtherKindContribution &&
                  data.type === "string" &&
                  data.value !== undefined
                ) {
                  subscriber.kindContributionModel.description.textModels
                    .find((t) => t.locale === language)
                    ?.model.setAndCommitInputValue(data.value);
                }
              },
            }) satisfies TableColumn<T>,
        ),
      ],
    },
  ]);
}

export function prepSignerColumns<T extends SubscriberForCapitalIncrease>(
  form: IncreaseOfShareCapitalForm,
  subscribers: T[],
): TableColumn<T>[] {
  if (form.isOtherSigningForAllSubscribers.value && !form.signersForAllSubscribers.length) {
    return [];
  }
  const commonSigners = form.isOtherSigningForAllSubscribers.value
    ? form.signersForAllSubscribers
    : undefined;
  const signersCount =
    commonSigners?.length ?? max(subscribers.map((s) => s.allSigners.length)) ?? 0;
  return [
    {
      name: "signer-management",
      label: { key: "decisions.increaseShareCapital.subscribersTable.manageSignatures" },
      align: "left",
      children: commonSigners
        ? commonSigners.flatMap<TableColumn<T>>((_, i) => [
            {
              name: `signer-${i}-name`,
              label: { key: "name" },
              getValue: () => commonSigners[i].name,
              getSortValue: () => commonSigners[i].name,
              isReadonly: () => true,
              align: "left",
              width: "lg",
            },
            {
              name: `signer-${i}-email`,
              label: { key: "email" },
              getValue: () => commonSigners[i].email,
              getSortValue: () => commonSigners[i].email,
              isReadonly: () => true,
              align: "left",
              width: "lg",
            },
          ])
        : [
            {
              name: "signer-choice",
              label: form.isSubscriptionForm.value
                ? {
                    key: "decisions.increaseShareCapital.subscribersTable.toSignSubscriptionForm",
                  }
                : {
                    key: "decisions.increaseShareCapital.subscribersTable.toSignGeneralMeetingMinutes",
                  },
              getValue: (subscriber) => ["select", prepSignerSelectProps(subscriber, subscribers)],
              isReadonly: () => !!commonSigners || form.isProjectLocked,
              align: "left",
              excelColumnConstraints: ({ t }) => [
                {
                  type: "list-constraint",
                  values: Object.values(SignerChoice).map((x) => t(signerChoiceLabel(x))),
                },
              ],
              exportToExcel: (subscriber, _, { t }) => ({
                type: "string",
                value: t(signerChoiceLabel(subscriber.signerChoiceModel.value)),
              }),
            },
            ...range(signersCount).flatMap<TableColumn<T>>((i) => [
              {
                name: `signer-${i}-name`,
                label: { key: "name" },
                exportToExcelLabel: prefixLabel(
                  { key: "decisions.increaseShareCapital.subscribersTable.manageSignatures" },
                  { key: "name" },
                ),
                getValue: (subscriber) =>
                  when(subscriber.allSigners[i]?.name, (model) => ["mtext-input", { model }]),
                getSortValue: (subscriber) => subscriber.allSigners[i]?.name.validatedValue,
                isReadonly: (subscriber) =>
                  form.isProjectLocked ||
                  !subscriber.allSigners[i] ||
                  subscriber.allSigners[i].isReadonly,
                align: "left",
                width: "lg",
                exportToExcel: (subscriber) => ({
                  type: "string",
                  value: subscriber.allSigners[i]?.name.validatedValue,
                  readonly: false,
                }),
                importFromExcel: (subscriber, data) => {
                  if (data.type === "string" && data.value !== undefined) {
                    const signer = subscriber.allSigners[i];
                    if (signer && !signer.isReadonly) {
                      signer.name.setAndCommitInputValue(data.value);
                    }
                  }
                },
              },
              {
                name: `signer-${i}-email`,
                label: { key: "email" },
                getValue: (subscriber) =>
                  when(subscriber.allSigners[i]?.email, (model) => ["mtext-input", { model }]),
                getSortValue: (subscriber) => subscriber.allSigners[i]?.email.validatedValue,
                isReadonly: (subscriber) =>
                  form.isProjectLocked ||
                  !subscriber.allSigners[i] ||
                  subscriber.allSigners[i].isReadonly,
                align: "left",
                width: "lg",
                exportToExcel: (subscriber) => ({
                  type: "string",
                  value: subscriber.allSigners[i]?.email.validatedValue,
                  readonly: false,
                }),
                importFromExcel: (subscriber, data) => {
                  if (data.type === "string" && data.value !== undefined) {
                    const signer = subscriber.allSigners[i];
                    if (signer && !signer.isReadonly) {
                      signer.email.setAndCommitInputValue(data.value);
                    }
                  }
                },
              },
            ]),
          ],
    },
  ];
}

function prepAddressColumns<T extends SubscriberForCapitalIncrease>(
  form: IncreaseOfShareCapitalForm,
): TableColumn<T>[] {
  if (!form.isDebtContributionActive && !form.isKindContributionActive) {
    return [];
  }
  return [
    {
      name: "address-info",
      label: "",
      children: [
        {
          name: "address",
          label: { key: "address" },
          getValue: (subscriber) => [
            "mtext-input",
            { model: subscriber.address, inputProps: { className: "min-w-[30ch]" } },
          ],
          getSortValue: (subscriber) => subscriber.address.validatedValue,
          align: "left",
          exportToExcel: (subscriber) => ({
            type: "string",
            value: subscriber.address.inputValue,
          }),
          importFromExcel: (subscriber, data) => {
            if (data.type === "string" && data.value !== undefined) {
              subscriber.address.setAndCommitInputValue(data.value);
            }
          },
        },
      ],
    },
  ];
}

function prepCashContributionColumns<T extends SubscriberForCapitalIncrease>(
  form: IncreaseOfShareCapitalForm,
): TableColumn<T>[] {
  if (!form.containsCash) {
    return [];
  }
  return [
    {
      name: "cash",
      label: "",
      children: [
        {
          name: "cash-contribution",
          label: { key: "decisions.increaseShareCapital.subscribersTable.cashContribution" },
          getValue: (subscriber) => ({ type: "number", value: subscriber.getRemaining(form) }),
          getSortValue: (subscriber) => subscriber.getRemaining(form),
          align: "right",
          isReadonly: () => true,
        },
      ],
    },
  ];
}

export const prepSubscribersEmptyStateActions = (
  form: IncreaseOfShareCapitalForm,
): EmptyStateProps["actions"] => [
  {
    type: "dropdown",
    key: "add-subsribers",
    props: prepAddSubscribersDropdown({ form, variant: "primary" }),
  },
];

function toAllocationStatus(
  issueMode: ShareIssueModeModel,
  progress: SubscriptionProgress,
): { className: string; text: TMessage } | undefined {
  if (issueMode.type === "fixed" && issueMode.calculateFromSubscriptions.value) {
    return undefined;
  }

  const remaining = progress.total - progress.issued;
  if (remaining > 0) {
    return {
      text: {
        key: "decisions.increaseShareCapital.subscribersTable.allocationStatus.remaining",
        params: { remaining: { type: "number", value: remaining } },
      },
      className: "subtle nowrap",
    };
  } else if (remaining < 0) {
    return {
      text: {
        key: "decisions.increaseShareCapital.subscribersTable.allocationStatus.exceeded",
        params: { exceeding: { type: "number", value: Math.abs(remaining) } },
      },
      className: "color-error nowrap",
    };
  } else {
    return {
      text: {
        key: "decisions.increaseShareCapital.subscribersTable.allocationStatus.completed",
      },
      className: "color-success nowrap",
    };
  }
}
